// ─────────────────────────────────────────────────────────────────────────────
// useUserMfa — the Security tab's data layer: the company directory paged from
// GET /users, plus the per-row PATCH /users/:id/mfa mutation.
//
// Unlike the other Settings tabs there is no Save Changes step — each toggle is
// its own request, so the hook tracks which row is in flight (`pendingId`) and
// flips optimistically, rolling back if the request fails. Search is server-side
// (debounced) to stay consistent with the Users page.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from "react";
import { getUsers, updateUserMfa } from "../../../services/usersService";

export const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

export function useUserMfa() {
  const [users,     setUsers]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [search,    setSearch]    = useState("");
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [pendingId, setPendingId] = useState(null);

  // Debounced copy of `search` — the input stays responsive while the fetch waits.
  const [query, setQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // A new query starts over at page 1, otherwise page 3 of the old result set
  // would be requested against the new one.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setPage(1);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUsers({ search: query || undefined, page, limit: PAGE_SIZE })
      .then((data) => {
        if (cancelled) return;
        setUsers(data?.users ?? []);
        setTotal(data?.total ?? 0);
      })
      .catch((err) => { if (!cancelled) setError(err.message || "Failed to load users."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, page]);

  const setMfa = useCallback(async (id, enabled) => {
    setError(null);
    setPendingId(id);
    // Optimistic — the switch moves under the cursor, not after the round-trip.
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, mfaEnabled: enabled } : u)));
    try {
      const updated = await updateUserMfa(id, enabled);
      // Reconcile with what the server actually stored.
      setUsers((prev) => prev.map((u) =>
        (u.id === id ? { ...u, mfaEnabled: updated?.mfaEnabled ?? enabled } : u)));
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, mfaEnabled: !enabled } : u)));
      setError(err.message || `Failed to ${enabled ? "enable" : "disable"} MFA.`);
    } finally {
      setPendingId(null);
    }
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return {
    users, total, loading, error, pendingId,
    search, setSearch,
    page, totalPages, setPage,
    setMfa,
  };
}
